import { useGetCasinoGamesInfiniteQuery, useGetLocalGameCategoriesQuery } from "@api";
import { getCategoryLocalizedName } from "@pages/casino/utils";
import { debounce } from "@utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getActiveLanguage } from "react-localize-redux";
import { useSelector } from "react-redux";

const PAGE_SIZE = 42;

const selectLanguage = (state) =>
	getActiveLanguage(state.localize)?.code || localStorage.getItem("lang") || "en";

const useController = ({ onSearchToggle }) => {
	const searchInputRef = useRef(null);
	const lastRequestedPageRef = useRef(1);
	const currentLanguage = useSelector(selectLanguage);

	const [isToggled, setIsToggled] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [page, setPage] = useState(1);

	const isSearchMode = searchValue.length >= 4;
	const shouldFetchGames = isToggled;
	const isIdleState = !shouldFetchGames;

	const queryArg = useMemo(() => {
		const base = {
			size: PAGE_SIZE,
			lang: currentLanguage,
		};

		if (isSearchMode) {
			const searchParams = {
				...base,
				search: searchValue,
				isSearch: true,
			};

			if (!selectedCategory) {
				return { ...searchParams, category: "search" };
			}

			return {
				...searchParams,
				local_game_category: selectedCategory,
			};
		}

		if (selectedCategory) {
			return { ...base, local_game_category: selectedCategory };
		}

		return base;
	}, [currentLanguage, isSearchMode, searchValue, selectedCategory]);

	const {
		data: casinoData,
		isLoading,
		isFetching,
		fetchNextPage,
		hasNextPage,
		isUninitialized,
	} = useGetCasinoGamesInfiniteQuery(queryArg, {
		skip: !isToggled || !shouldFetchGames,
		refetchOnMountOrArgChange: true,
	});

	const { data: gameCategories } = useGetLocalGameCategoriesQuery(
		{ size: 1000, isActive: true },
		{ skip: !isToggled }
	);

	const categories = useMemo(() => {
		const allCategories = gameCategories?.categories || [];

		return allCategories.map((category) => ({
			key: category.name,
			value: getCategoryLocalizedName(category, currentLanguage),
		}));
	}, [gameCategories, currentLanguage]);

	const totalGames = casinoData?.pages?.[0]?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalGames / PAGE_SIZE));
	const games = shouldFetchGames ? casinoData?.pages?.[page - 1]?.games ?? [] : [];
	const isSearchLoading =
		isSearchMode &&
		(isUninitialized ||
			isLoading ||
			(isFetching && !casinoData?.pages?.[page - 1]?.games?.length));
	const isFilterFetching =
		!isSearchMode &&
		shouldFetchGames &&
		(isUninitialized || isLoading || isFetching) &&
		!games.length;

	const debouncedSearch = useMemo(
		() =>
			debounce((value) => {
				setSearchValue(value?.trim() ?? "");
			}, 400),
		[]
	);

	const handleClose = useCallback(() => {
		setIsToggled(false);
		onSearchToggle?.(false);
		window.dispatchEvent(new CustomEvent("closeCasinoSearchModal"));
	}, [onSearchToggle]);

	const handleToggleChange = useCallback(() => {
		setIsToggled((prev) => {
			if (prev) return prev;

			onSearchToggle?.(true);
			return true;
		});
	}, [onSearchToggle]);

	const handleSearchInput = useCallback(
		(event) => {
			const value = event?.target?.value ?? "";

			if (!value.trim()) {
				setSearchValue("");
				return;
			}

			debouncedSearch(value);
		},
		[debouncedSearch]
	);

	const handleClearInput = useCallback(() => {
		setSearchValue("");
		if (searchInputRef.current) {
			searchInputRef.current.value = "";
			searchInputRef.current.focus?.();
		}
	}, []);

	const handleCategorySelect = useCallback((categoryKey) => {
		setSelectedCategory(categoryKey);
	}, []);

	const handlePageChange = useCallback(
		(nextPage) => {
			const safePage = Math.min(Math.max(1, nextPage), totalPages);
			setPage(safePage);
		},
		[totalPages]
	);

	useEffect(() => {
		setPage(1);
		lastRequestedPageRef.current = 1;
	}, [searchValue, selectedCategory, queryArg]);

	useEffect(() => {
		if (!isToggled || !casinoData) return;

		const loadedPages = casinoData.pages?.length ?? 0;
		if (
			page > loadedPages &&
			hasNextPage &&
			!isFetching &&
			lastRequestedPageRef.current !== page
		) {
			lastRequestedPageRef.current = page;
			fetchNextPage();
		}
	}, [page, casinoData, hasNextPage, fetchNextPage, isToggled, isFetching]);

	useEffect(() => {
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, totalPages]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				handleClose();
			}
		};

		if (isToggled) {
			window.addEventListener("keydown", handleKeyDown);
			requestAnimationFrame(() => {
				searchInputRef.current?.focus?.();
			});
		} else {
			setSearchValue("");
			setSelectedCategory(null);
			setPage(1);
			if (searchInputRef.current) {
				searchInputRef.current.value = "";
			}
			window.removeEventListener("keydown", handleKeyDown);
		}

		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [isToggled, handleClose]);

	useEffect(() => {
		const handleCloseEvent = () => {
			setIsToggled(false);
		};

		window.addEventListener("closeCasinoSearchModal", handleCloseEvent);
		return () => window.removeEventListener("closeCasinoSearchModal", handleCloseEvent);
	}, []);

	useEffect(() => {
		const openByEvent = (event) => {
			const shouldOpen = event?.detail?.open ?? true;
			if (!shouldOpen) return;

			setIsToggled(true);
		};

		window.addEventListener("openCasinoSearchModal", openByEvent);
		return () => window.removeEventListener("openCasinoSearchModal", openByEvent);
	}, []);

	return {
		refs: { searchInputRef },
		state: {
			isToggled,
			searchValue,
			isSearchMode,
			isIdleState,
			shouldFetchGames,
			selectedCategory,
			page,
			totalPages,
			totalGames,
			games,
			categories,
			isLoadingGames: isSearchLoading,
			isFilterFetching,
		},
		handlers: {
			handleToggleChange,
			handleClose,
			handleSearchInput,
			handleClearInput,
			handleCategorySelect,
			handlePageChange,
		},
	};
};

export default useController;
